/**
 * conference-speakers block (1 use)
 *
 * Conference page with hero background, hardcoded event details,
 * and an interactive quiz game (originally Alpine.js).
 * The quiz game is rendered with React useState replacing Alpine.js.
 *
 * Hugo template: blocks/conference-speakers.html
 */
import { useState } from "react";

interface QuizQuestion {
  image?: string;
  correct_answer?: boolean;
  label?: string;
}

interface QuizResult {
  image?: string;
  heading?: string;
  copy?: string;
}

export function ConferenceSpeakers({
  hero_bg,
  hero_bg_lg,
  hero_image,
  heading,
  copy,
  location_badges,
  quiz_heading_image,
  questions,
  results,
}: {
  hero_bg?: string;
  hero_bg_lg?: string;
  hero_image?: string;
  heading?: string;
  copy?: string;
  location_badges?: string[];
  quiz_heading_image?: string;
  questions?: QuizQuestion[];
  results?: QuizResult[];
}) {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [score, setScore] = useState(0);
  const [showResult, setShowResult] = useState(false);

  const handleAnswer = (answer: boolean, correctAnswer?: boolean) => {
    if (answer === correctAnswer) {
      setScore((s) => s + 1);
    }
    if (questions && currentQuestion < questions.length - 1) {
      setCurrentQuestion((q) => q + 1);
    } else {
      setShowResult(true);
    }
  };

  const resetQuiz = () => {
    setCurrentQuestion(0);
    setScore(0);
    setShowResult(false);
  };

  return (
    <div>
      {/* Hero section */}
      <div className="px-4 md:px-8 py-20 md:py-4 bg-[#161415] relative overflow-hidden">
        <div className="absolute inset-0">
          {hero_bg_lg && (
            <img
              src={hero_bg_lg}
              alt=""
              className="w-full h-full object-cover hidden md:block"
              loading="eager"
            />
          )}
          {hero_bg && (
            <img
              src={hero_bg}
              alt=""
              className="w-full h-full object-cover md:hidden"
              loading="eager"
            />
          )}
        </div>
        {hero_image && (
          <div className="max-w-[754px] mx-auto relative">
            <img src={hero_image} alt="" className="w-full" loading="eager" />
          </div>
        )}
      </div>

      {/* Content section */}
      <div className="px-4 md:px-8 pt-16 pb-16 lg:pb-28">
        <div className="max-w-screen-xl mx-auto">
          {heading && (
            <h1 className="font-display text-charcoal font-bold text-25 mt-4 lg:text-31 max-w-3xl">
              {heading}
            </h1>
          )}
          {copy && (
            <p className="font-body text-gray-80 text-25 mt-4 md:mt-5 max-w-4xl">
              {copy}
            </p>
          )}
          {location_badges && location_badges.length > 0 && (
            <ul className="flex flex-wrap gap-4 mt-6 max-w-xl">
              {location_badges.map((badge, i) => (
                <li
                  key={i}
                  className="font-medium text-base text-teal-dark bg-teal-dark/10 rounded-full px-3.5 py-1.5 leading-tight"
                >
                  {badge}
                </li>
              ))}
            </ul>
          )}

          {/* Quiz section */}
          {questions && questions.length > 0 && (
            <div className="mt-16 bg-teal-dark rounded-2xl overflow-hidden">
              {quiz_heading_image && (
                <div className="flex justify-center pt-8">
                  <img
                    src={quiz_heading_image}
                    alt=""
                    className="h-auto max-w-sm"
                  />
                </div>
              )}
              <div className="py-10 px-6 sm:px-14">
                {!showResult ? (
                  <div>
                    <p className="text-center text-white/60 text-sm mb-6">
                      Question {currentQuestion + 1} of {questions.length}
                    </p>
                    {questions[currentQuestion] && (
                      <div className="text-center">
                        {questions[currentQuestion].image && (
                          <div className="flex justify-center mb-8">
                            <img
                              src={questions[currentQuestion].image}
                              alt=""
                              className="h-48 w-auto"
                            />
                          </div>
                        )}
                        {questions[currentQuestion].label && (
                          <p className="font-body text-white text-xl font-medium mb-8">
                            {questions[currentQuestion].label}
                          </p>
                        )}
                        <div className="flex gap-4 justify-center">
                          <button
                            onClick={() =>
                              handleAnswer(
                                true,
                                questions[currentQuestion].correct_answer
                              )
                            }
                            type="button"
                            className="px-10 py-3 bg-teal-cta text-white font-body font-semibold rounded-lg hover:bg-teal-cta-hover transition-colors"
                          >
                            Human
                          </button>
                          <button
                            onClick={() =>
                              handleAnswer(
                                false,
                                questions[currentQuestion].correct_answer
                              )
                            }
                            type="button"
                            className="px-10 py-3 bg-pink-accent text-charcoal font-body font-semibold rounded-lg hover:bg-pink-accent/80 transition-colors"
                          >
                            AI
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center">
                    {results && results[score] && (
                      <div>
                        {results[score].image && (
                          <div className="flex justify-center mb-6">
                            <img
                              src={results[score].image}
                              alt=""
                              className="max-w-sm"
                            />
                          </div>
                        )}
                        {results[score].heading && (
                          <h3 className="font-display text-white text-2xl font-bold mb-4">
                            {results[score].heading}
                          </h3>
                        )}
                        {results[score].copy && (
                          <p className="text-white/80 mb-8">
                            {results[score].copy}
                          </p>
                        )}
                      </div>
                    )}
                    <p className="text-white text-lg mb-4">
                      Score: {score} / {questions.length}
                    </p>
                    <button
                      onClick={resetQuiz}
                      className="px-8 py-3 bg-cream text-charcoal font-body font-medium rounded-10 hover:bg-white transition-colors"
                    >
                      Play Again
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
